import { useMutation, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from '@/constants';
import { handleResponse, get, post, put, del } from './base.service';
import { handleError as appHandleError } from '@/utils/errorHandler';
import { queryKeys } from './queryKeys';
import type { ApiResponse } from './types';

export const useGetLessonListQuery = (payload: string) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(`${API_ENDPOINTS.DAILY_ROSTER}?${payload}`);
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        return { success: false, data: null };
      }
    },
    queryKey: queryKeys.roster.daily(payload),
    refetchOnWindowFocus: false
  });

type CreateLessonPayload = Record<string, unknown>;

export const useCreateNewLessonMutation = () =>
  useMutation<ApiResponse, unknown, CreateLessonPayload>({
    mutationFn: (payload: CreateLessonPayload) => {
      return post(`${API_ENDPOINTS.DAILY_ROSTER}`, payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useUndoDailyRosterLessonMutation = () =>
  useMutation<ApiResponse, unknown, void>({
    mutationFn: () => {
      return get(`${API_ENDPOINTS.DAILY_ROASTER_UNDO_ROSTER}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useCheckLessonAvailabilityMutation = () =>
  useMutation<ApiResponse, unknown, string>({
    mutationFn: (payload: string) => {
      return get(`${API_ENDPOINTS.CHECK_LESSON_AVAILABILITY}?${payload}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useCheckCarAvailabilityMutation = () =>
  useMutation<ApiResponse, unknown, string>({
    mutationFn: (payload: string) => {
      return get(`${API_ENDPOINTS.CHECK_CAR_AVAILABILITY}?${payload}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useUpdateLessonMutation = () =>
  useMutation<
    ApiResponse,
    unknown,
    { id: number | string } & Record<string, unknown>
  >({
    mutationFn: (
      payload: { id: number | string } & Record<string, unknown>
    ) => {
      return put(`${API_ENDPOINTS.DAILY_ROSTER}/${payload.id}`, payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useDeleteLessonMutation = () =>
  useMutation<ApiResponse, unknown, number>({
    mutationFn: (id: number) => {
      return del(`${API_ENDPOINTS.DAILY_ROSTER}/${id}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useUpdateProcessAndUnprocessedLessonsMutation = () =>
  useMutation<ApiResponse, unknown, { id: number | string | undefined }>({
    mutationFn: (payload: { id: number | string | undefined }) => {
      return put(
        `${API_ENDPOINTS.DAILY_ROASTER_PROCESS_UNPROCESSED}/${payload.id ?? ''}`,
        payload
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetLessonEntriesQuery = (payload: string) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(
          `${API_ENDPOINTS.LESSON_ENTRIES}?${payload}`
        );
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        return { success: false, data: null };
      }
    },
    queryKey: queryKeys.roster.entries(payload),
    refetchOnWindowFocus: false
  });

export const useGetAgentCommissionMutation = () =>
  useMutation<
    ApiResponse,
    unknown,
    {
      schedule_lesson_id: number | string;
      agent_id: number | string;
      lesson_code_id: number | string;
    }
  >({
    mutationFn: (params: {
      schedule_lesson_id: number | string;
      agent_id: number | string;
      lesson_code_id: number | string;
    }) => {
      const query = `schedule_lesson_id=${params.schedule_lesson_id}&agent_id=${params.agent_id}&lesson_code_id=${params.lesson_code_id}`;
      return get(`${API_ENDPOINTS.AGENT_COMMISSION}?${query}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useInstructorListMutation = () =>
  useMutation<
    ApiResponse,
    unknown,
    { date: string; lesson_id: number; time: string }
  >({
    mutationFn: async (params: {
      date: string;
      lesson_id: number;
      time: string;
    }): Promise<ApiResponse> => {
      const query = `date=${encodeURIComponent(params.date)}&lesson_id=${params.lesson_id}&time=${encodeURIComponent(params.time)}&status=Active&sortBy=name&sortOrder=ASC`;
      const resp = await get(`${API_ENDPOINTS.INSTRUCTOR_LIST}?${query}`);
      return handleResponse(resp) as ApiResponse;
    },
    onError: error => appHandleError(error)
  });

export const useGetProcessedAgentCommissionMutation = () =>
  useMutation<
    ApiResponse,
    unknown,
    {
      schedule_lesson_id: number;
      agent_id: number;
      lesson_code_id: number;
      am?: number;
      pm?: number;
      fd?: number;
    }
  >({
    mutationFn: (params: {
      schedule_lesson_id: number;
      agent_id: number;
      lesson_code_id: number;
      am?: number;
      pm?: number;
      fd?: number;
    }) => {
      let query = `schedule_lesson_id=${params.schedule_lesson_id}&agent_id=${params.agent_id}&lesson_code_id=${params.lesson_code_id}`;
      if (params.am !== undefined) query += `&am=${params.am}`;
      if (params.pm !== undefined) query += `&pm=${params.pm}`;
      if (params.fd !== undefined) query += `&fd=${params.fd}`;
      return get(`${API_ENDPOINTS.PROCESSED_AGENT_COMMISSION}?${query}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useUpdateParticipantMutation = () =>
  useMutation<
    ApiResponse,
    unknown,
    { id: number | string } & Record<string, unknown>
  >({
    mutationFn: (
      payload: { id: number | string } & Record<string, unknown>
    ) => {
      return put(`${API_ENDPOINTS.PARTICIPANT_UPDATE}/${payload.id}`, payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useCreateParticipantMutation = () =>
  useMutation<
    ApiResponse,
    unknown,
    { id: number | string } & Record<string, unknown>
  >({
    mutationFn: (
      payload: { id: number | string } & Record<string, unknown>
    ) => {
      return put(`${API_ENDPOINTS.PARTICIPANT}/${payload.id}`, payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetParticipantsListQuery = (
  scheduleLessonId: number | null,
  queryString: string
) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(
          `${API_ENDPOINTS.PARTICIPANTS_LIST}/${scheduleLessonId}?${queryString}`
        );
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        return { success: false, data: null };
      }
    },
    queryKey: queryKeys.roster.participants(scheduleLessonId, queryString),
    enabled: !!scheduleLessonId,
    refetchOnWindowFocus: false
  });

export const useDeleteParticipantMutation = () =>
  useMutation<ApiResponse, unknown, number>({
    mutationFn: (id: number) => {
      return del(`${API_ENDPOINTS.PARTICIPANT}/${id}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetInstructorRosterListQuery = (params: {
  id?: number | string;
  queryStr: string;
}) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(
          `${API_ENDPOINTS.INSTRUCTOR_ROSTER_LESSON}/${params.id}?${params.queryStr}`
        );
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        return { success: false, data: null };
      }
    },
    queryKey: queryKeys.roster.instructorRoster(
      params.id ?? null,
      params.queryStr
    ),
    enabled:
      params.id !== undefined &&
      params.id !== null &&
      `${params.id}`.length > 0,
    refetchOnWindowFocus: false
  });
